const VOLUME_INDICATORS = new Set([
  'AccumulationDistribution', 'ChaikinMoneyFlow', 'ChaikinOscillator',
  'OnBalanceVolume', 'PVT', 'VWAP', 'Volume', 'VolumeProfile',
]);
const installed = new WeakMap();

function prefixStatus(prices, volumes) {
  let availableBars = 0;
  let reason = 'live';
  for (; availableBars < prices.length; availableBars++) {
    const price = prices[availableBars];
    const volume = volumes[availableBars];
    if (!volume || price.get('valueX') !== volume.get('valueX')) {
      reason = 'alignment';
      break;
    }
    const value = volume.get('valueY');
    if (volume.dataContext?.VolumeAvailable === false
      || !Number.isFinite(value) || value < 0) {
      reason = availableBars === prices.length - 1 ? 'live' : 'history';
      break;
    }
  }
  return { unavailable: availableBars < prices.length, availableBars,
    totalBars: prices.length, reason: availableBars < prices.length ? reason : null };
}

// Only indicator calculations see the prefix. Chart/volume series, data items,
// axis domains and event handlers retain their full data and original identity.
function prefixView(series, length) {
  const items = series?.dataItems?.slice(0, length) || [];
  return new Proxy(series || {}, {
    get(target, key) {
      if (key === 'dataItems') return items;
      if (key === 'startIndex' || key === 'endIndex') {
        return () => Math.max(0, Math.min(length, target[key]?.() ?? (key === 'endIndex' ? length : 0)));
      }
      if (key === 'getPrivate') {
        return (name, fallback) => {
          const value = target.getPrivate?.(name, fallback) ?? fallback;
          return name === 'adjustedStartIndex' ? Math.max(0, Math.min(length, value)) : value;
        };
      }
      const value = Reflect.get(target, key, target);
      return typeof value === 'function' ? value.bind(target) : value;
    },
  });
}

function clearOutput(indicator) {
  for (const series of [indicator.series, indicator.upSeries]) {
    if (!series) continue;
    if (series.data?.length || series.dataItems?.length) series.data?.clear();
  }
}

// Component.updateData trims rendered dataItems but leaves the source ListData
// tail behind (and an empty update retains its first item). Trim through the
// public list first so later recovery cannot resurrect an unavailable bar.
function prepareOutput(indicator, originalPrepare, args) {
  const restore = [];
  for (const series of new Set([indicator.series, indicator.upSeries])) {
    if (typeof series?.updateData !== 'function' || !series.data) continue;
    const ownUpdate = Object.getOwnPropertyDescriptor(series, 'updateData');
    const update = series.updateData;
    series.updateData = function (rows) {
      if (this.dataItems && this.dataItems.length !== this.data.length) {
        // Repair output already shortened by the library before this guard ran.
        this.data.setAll(rows);
        return;
      }
      while (this.data.length > rows.length) this.data.removeIndex(this.data.length - 1);
      return update.call(this, rows);
    };
    restore.push(() => {
      if (ownUpdate) Object.defineProperty(series, 'updateData', ownUpdate);
      else delete series.updateData;
    });
  }
  try { return originalPrepare.apply(indicator, args); }
  finally { restore.forEach(reset => reset()); }
}

// amCharts treats missing volume as 1 in several indicators. Restrict inputs to
// the complete prefix instead: no synthetic volume and no compression of gaps.
export function configureVolumeIndicatorData(indicator, onStatus = () => {}) {
  const name = indicator?.constructor?.className;
  if (!VOLUME_INDICATORS.has(name) || typeof indicator.prepareData !== 'function') return { dispose() {} };
  const prior = installed.get(indicator);
  if (prior) return prior;
  const originalPrepare = indicator.prepareData;
  let previousStatus;
  const publish = status => {
    const key = JSON.stringify(status);
    if (key !== previousStatus) {
      previousStatus = key;
      onStatus(status);
    }
  };
  const prepare = function (...args) {
    if (name === 'AccumulationDistribution' && !this.get('useVolume')) {
      publish({ unavailable: false, availableBars: 0, totalBars: 0, reason: null });
      return prepareOutput(this, originalPrepare, args);
    }
    const stock = this.get('stockSeries');
    const volume = this.get('volumeSeries');
    const status = prefixStatus(stock?.dataItems || [], volume?.dataItems || []);
    publish(status);
    if (status.availableBars === 0) {
      clearOutput(this);
      return;
    }
    if (!status.unavailable) return prepareOutput(this, originalPrepare, args);
    const stockView = prefixView(stock, status.availableBars);
    const volumeView = prefixView(volume, status.availableBars);
    const ownGet = Object.getOwnPropertyDescriptor(this, 'get');
    const get = this.get;
    this.get = function (key, fallback) {
      if (key === 'stockSeries') return stockView;
      if (key === 'volumeSeries') return volumeView;
      return get.call(this, key, fallback);
    };
    try { return prepareOutput(this, originalPrepare, args); }
    finally {
      if (ownGet) Object.defineProperty(this, 'get', ownGet);
      else delete this.get;
    }
  };
  indicator.prepareData = prepare;
  const disposer = {
    dispose() {
      if (indicator.prepareData === prepare) indicator.prepareData = originalPrepare;
      installed.delete(indicator);
    },
  };
  installed.set(indicator, disposer);
  return disposer;
}
